/**
 * The SI prefixes, quecto (10^−30) to quetta (10^30).
 *
 * The list is Table 7 of the SI Brochure (9th edition, BIPM, 2019; updated with the 2022
 * additions): ronna (R, 10^27) and quetta (Q, 10^30) above yotta, and ronto (r, 10^−27) and
 * quecto (q, 10^−30) below yocto, adopted by the 27th General Conference on Weights and
 * Measures (CGPM) in November 2022. Capital R and Q are used for the large prefixes and
 * lower case r and q for the small ones, so no symbol clashes with another prefix.
 *
 * A prefix multiplies a unit by an exact power of ten. The kilogram is the one odd case:
 * its name already carries a prefix, so the prefixes attach to the gram (a milligram is
 * 10^−3 g, never a "microkilogram").
 */

import { powerOfTen, type Ratio } from "@/lib/tool-kit/calendars/exact";

export interface SiPrefix {
  id: string;
  symbol: string;
  exponent: number;
  /** The year the CGPM added it, when that is a recent addition. */
  addedIn?: number;
}

export const SI_PREFIXES: readonly SiPrefix[] = [
  { id: "quetta", symbol: "Q", exponent: 30, addedIn: 2022 },
  { id: "ronna", symbol: "R", exponent: 27, addedIn: 2022 },
  { id: "yotta", symbol: "Y", exponent: 24 },
  { id: "zetta", symbol: "Z", exponent: 21 },
  { id: "exa", symbol: "E", exponent: 18 },
  { id: "peta", symbol: "P", exponent: 15 },
  { id: "tera", symbol: "T", exponent: 12 },
  { id: "giga", symbol: "G", exponent: 9 },
  { id: "mega", symbol: "M", exponent: 6 },
  { id: "kilo", symbol: "k", exponent: 3 },
  { id: "hecto", symbol: "h", exponent: 2 },
  { id: "deca", symbol: "da", exponent: 1 },
  { id: "none", symbol: "", exponent: 0 },
  { id: "deci", symbol: "d", exponent: -1 },
  { id: "centi", symbol: "c", exponent: -2 },
  { id: "milli", symbol: "m", exponent: -3 },
  { id: "micro", symbol: "µ", exponent: -6 },
  { id: "nano", symbol: "n", exponent: -9 },
  { id: "pico", symbol: "p", exponent: -12 },
  { id: "femto", symbol: "f", exponent: -15 },
  { id: "atto", symbol: "a", exponent: -18 },
  { id: "zepto", symbol: "z", exponent: -21 },
  { id: "yocto", symbol: "y", exponent: -24 },
  { id: "ronto", symbol: "r", exponent: -27, addedIn: 2022 },
  { id: "quecto", symbol: "q", exponent: -30, addedIn: 2022 },
];

const PLAIN_PREFIX: SiPrefix = { id: "none", symbol: "", exponent: 0 };

export function prefixFactor(prefix: SiPrefix): Ratio {
  return powerOfTen(prefix.exponent);
}

/**
 * The prefix that puts a value in the range 1 to 999.99… (the "engineering" choice), among
 * the prefixes in steps of three powers of ten plus the plain unit. Hecto, deca, deci and
 * centi are left out because they are not engineering prefixes. `exponentOfValue` is the
 * power of ten of the value's first digit, so 4700 has exponent 3.
 */
export function engineeringPrefix(exponentOfValue: number): SiPrefix {
  const steps = SI_PREFIXES.filter((prefix) => prefix.exponent % 3 === 0);
  const target = Math.floor(exponentOfValue / 3) * 3;
  const clamped = Math.max(-30, Math.min(30, target));
  return steps.find((prefix) => prefix.exponent === clamped) ?? PLAIN_PREFIX;
}
